import {FC, useState, useCallback, useMemo, useEffect} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    UsersAndGroupsMultiSelectPopover,
    SelectedItem,
    UserSelectionItem
} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import {CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {AvatarGroup} from '@/components/commons/avatar-group/AvatarGroup'
import {ConversationMember} from '@/features/conversations/types'
import {useUpdateConversationMembers} from '@/features/conversations/query/useUpdateConversationMembers'

import {Text} from '@/components/ui/text/Text'
import {useGetUserGroups} from '@/features/user-groups/queries/useGetUserGroups'
import {useTheme} from 'styled-components'
import {useTranslation} from '@/translations/i18n'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'

interface ConversationParticipantsProps {
    conversationId: string
    participants: ConversationMember[]
    patientId: string
}

export const ConversationParticipants: FC<ConversationParticipantsProps> = ({
    conversationId,
    participants,
    patientId
}) => {
    const {t} = useTranslation()
    const {palette, spacing} = useTheme()
    // Get user groups to help determine which groups are selected
    const userGroupsQuery = useGetUserGroups({})

    // Helper function to determine selected items based on participants
    // Now we only work with users, expanding groups to their individual members
    const determineSelectedItems = useCallback(
        (currentParticipants: ConversationMember[]) => {
            const selectedItems: SelectedItem[] = []

            // Process all participants and expand groups to their individual members
            for (const participant of currentParticipants) {
                if (participant.type === 'group') {
                    // Expand group to its individual members
                    const group = userGroupsQuery.data?.data?.find(g => g.id === participant.id)
                    if (group?.members) {
                        group.members.forEach(member => {
                            selectedItems.push({
                                type: 'user',
                                id: member.id
                            })
                        })
                    }
                } else if (participant.type === 'user') {
                    // Add individual user
                    selectedItems.push({
                        type: 'user',
                        id: participant.id
                    })
                }
            }

            return selectedItems
        },
        [userGroupsQuery.data?.data]
    )

    // Track selected items (users and groups)
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>(() => {
        return determineSelectedItems(participants)
    })

    // Store actual item data for display (so we don't lose names when selecting)
    const [selectedItemsData, setSelectedItemsData] = useState<Map<string, {name: string; type: 'user' | 'group'}>>(
        () => {
            const map = new Map()
            participants.forEach(p => {
                if (p.type === 'user' && p.fullName) {
                    map.set(p.id, {name: p.fullName, type: 'user'})
                } else if (p.type === 'group' && p.name) {
                    map.set(p.id, {name: p.name, type: 'group'})
                }
            })
            return map
        }
    )

    // We don't need a separate selectedUsers state - we'll compute display members from selectedItems

    const updateMembersMutation = useUpdateConversationMembers(patientId)

    // Update local state when participants prop changes (after API success)
    useEffect(() => {
        const newSelectedItems = determineSelectedItems(participants)
        setSelectedItems(newSelectedItems)

        // Update stored data for participants
        const newDataMap = new Map<string, {name: string; type: 'user' | 'group'}>()
        participants.forEach(p => {
            if (p.type === 'user' && p.fullName) {
                newDataMap.set(p.id, {name: p.fullName, type: 'user'})
            } else if (p.type === 'group' && p.name) {
                newDataMap.set(p.id, {name: p.name, type: 'group'})
            }
        })
        setSelectedItemsData(newDataMap)
    }, [participants])

    // Calculate display items for avatars (now only users)
    const currentDisplayItems = useMemo(() => {
        const displayItems: Array<{
            id: string
            type: 'user' | 'group'
            name: string
            avatarUrl?: string | null
        }> = []

        selectedItems.forEach(item => {
            const itemData = selectedItemsData.get(item.id)
            if (itemData) {
                displayItems.push({
                    id: item.id,
                    type: 'user', // Always 'user' now since we only work with users
                    name: itemData.name,
                    avatarUrl: null
                })
            }
        })

        return displayItems
    }, [selectedItems, selectedItemsData])

    // Check if there are any changes from the original state
    const hasChanges = useMemo(() => {
        const originalSelectedItems = determineSelectedItems(participants)

        // Compare the current selectedItems with the original ones
        if (originalSelectedItems.length !== selectedItems.length) return true

        for (const originalItem of originalSelectedItems) {
            const exists = selectedItems.some(item => item.type === originalItem.type && item.id === originalItem.id)
            if (!exists) return true
        }

        for (const currentItem of selectedItems) {
            const exists = originalSelectedItems.some(
                item => item.type === currentItem.type && item.id === currentItem.id
            )
            if (!exists) return true
        }

        return false
    }, [participants, selectedItems, determineSelectedItems])

    const handleToggleClick = useCallback(
        (users: UserSelectionItem[], itemData: CombinedItem) => {
            // Determine if we're adding or removing based on the first user's selection status
            const firstUser = users[0]
            if (!firstUser) return

            const isAdding = !selectedItems.some(item => item.type === 'user' && item.id === firstUser.id)

            // Update selected items - always work with users only
            setSelectedItems(prev => {
                if (isAdding) {
                    // Add all users from the selection
                    const newUserItems = users.map(user => ({
                        type: 'user' as const,
                        id: user.id
                    }))

                    // Remove any existing items that might conflict
                    const filteredPrev = prev.filter(
                        item => !users.some(user => item.type === 'user' && item.id === user.id)
                    )

                    return [...filteredPrev, ...newUserItems]
                } else {
                    // Remove all users from the selection
                    return prev.filter(item => !users.some(user => item.type === 'user' && item.id === user.id))
                }
            })

            // Update item data for display
            setSelectedItemsData(prev => {
                const newMap = new Map(prev)

                if (isAdding) {
                    // Add data for all users
                    users.forEach(user => {
                        const name =
                            itemData.type === 'user'
                                ? itemData.fullName || itemData.name
                                : itemData.members?.find(m => m.id === user.id)?.fullName || `User ${user.id}`
                        newMap.set(user.id, {
                            name: name,
                            type: 'user' as const
                        })
                    })
                } else {
                    // Remove data for all users
                    users.forEach(user => {
                        newMap.delete(user.id)
                    })
                }
                return newMap
            })
        },
        [selectedItems]
    )

    const handlePopoverClose = useCallback(() => {
        if (!hasChanges) {
            return // No changes to apply
        }

        // Create the final members list for the API (only users now)
        const finalMembers = selectedItems.map(item => ({
            type: 'user' as const,
            id: item.id
        }))

        // Make the API call with the selected users
        updateMembersMutation.mutate({
            conversationId,
            payload: {members: finalMembers}
        })
    }, [hasChanges, selectedItems, conversationId, updateMembersMutation])

    return (
        <Flexbox direction="column" fullWidth>
            <Flexbox direction="column" gap={2} fullWidth>
                <UsersAndGroupsMultiSelectPopover
                    currentDisplayItems={currentDisplayItems}
                    selectedItems={selectedItems}
                    toggleClick={handleToggleClick}
                    onClose={handlePopoverClose}
                    trigger={
                        <Flexbox
                            gap={2}
                            direction="row"
                            align="center"
                            justify="start"
                            fullWidth
                            style={{cursor: 'pointer'}}
                        >
                            <Tooltip
                                content={
                                    <Flexbox direction="column" gap={2}>
                                        {currentDisplayItems.map(item => (
                                            <Text size="textXs" palette="neutral" shade="700">
                                                {item.name}
                                            </Text>
                                        ))}
                                    </Flexbox>
                                }
                                isOpenOnClickEnabled={false}
                                contentProps={{side: 'bottom'}}
                                variant="light"
                            >
                                <AvatarGroup>
                                    {currentDisplayItems.slice(0, 3).map(item => (
                                        <AvatarGroup.Item
                                            key={item.id}
                                            name={item.name}
                                            imageUrl={item.avatarUrl || undefined}
                                            size="xs"
                                        />
                                    ))}
                                </AvatarGroup>
                            </Tooltip>

                            <Text size="textXs" palette="neutral" shade="700">
                                {currentDisplayItems.length === 0
                                    ? t('conversations:select_participants')
                                    : currentDisplayItems.length === 1
                                      ? currentDisplayItems[0].name
                                      : currentDisplayItems.length === 2
                                        ? t('conversations:name_and_name', {
                                              name1: currentDisplayItems[0].name,
                                              name2: currentDisplayItems[1].name
                                          })
                                        : t('conversations:name_and_others', {
                                              name: currentDisplayItems[0].name,
                                              count: currentDisplayItems.length - 1
                                          })}
                                {hasChanges && (
                                    <span
                                        style={{
                                            marginLeft: spacing,
                                            fontSize: 'inherit',
                                            fontWeight: '500',
                                            color: palette.primary[600]
                                        }}
                                    >
                                        {t('conversations:edited')}
                                    </span>
                                )}
                            </Text>
                        </Flexbox>
                    }
                />
            </Flexbox>
        </Flexbox>
    )
}
